// ModernBrushPanelFlat — Modern「ブラシ」パネルの**フォン専用**バリアント (全ブラシを
// タブ無しで並べる旧実装、凍結維持).
//
// 背景 (docs/_wip/modern-brush-panel-2026-08-17.md §14「タブ構成」consult 2026-08-18):
// デスクトップ版 (`ModernBrushPanel.tsx`) は §14 でタブ構成 (線画/塗り/質感/マイブラシ)
// に作り直したが、**フォン (`ModernPhoneShell`) はタブを持ち込まない**。理由 (§14 実装
// 注記「シートにはタブを持ち込まないこと」): `ModernPhoneShell` のボトムシートは下部
// ナビ (入口タップ) + シート (内容) の**既に 2 段ナビ**であり、タブを足すと 3 段ナビに
// なって縦溢れ問題がむしろ悪化する。フォンは「縦に長い・スクロールで見る」ことが前提の
// 画面設計 (シート自体が `max-h-[55dvh] overflow-y-auto`) なので、タブ化の動機
// (常設パネルの縦圧迫を解消) がそもそも成立しない。
//
// このファイルは **§14 着手前の `ModernBrushPanel.tsx` をそのまま複製**したもの
// (グループ見出し + マイブラシ常設フッタ、旧「実機フィードバック後の構造」)。
// 挙動・DOM 構造・テストとも変更しない (`ModernBrushPanelFlat.test.tsx` は旧
// `ModernBrushPanel.test.tsx` の複製)。`ModernLayout.tsx` は `isMobile` のときだけ本
// コンポーネントを使う (デスクトップは `ModernBrushPanel` = タブ版)。
//
// **共有部分**: item のスタイル・サムネ計算 (リスト表示固定) は `ModernBrushPanel`
// (タブ版) と重複させず `brushPanelShared.ts` から輸入する (= 見た目の差分ゼロを保証)。
// **構成 (グループ + 見出し + マイブラシ常設フッタ) は本ファイル固有**で、共有モジュール
// には持ち込まない (= タブ版の再構成の影響を受けない)。
//
// ## ヘッダ (見出し + リスト/グリッド切替) の撤去、リスト表示固定 (2026-08-19)
//
// `ModernBrushPanel.tsx` (タブ版) と同時に撤去する (根拠・詳細はそちらのファイル冒頭
// コメント参照: `<h2>` 撤去は「中身が自明で見出しが情報を足さない」+ `ModernColorPanel`
// の先行事例、リスト/グリッド撤去はタブ化で 1 タブ最大 4 item になりグリッドの存在理由が
// 消えたため)。フォン版もタブ版と同じく `viewToggleClass`/`BrushPanelView` 依存を使って
// いたため、**片方だけ撤去すると desktop/phone で挙動が食い違う**ので同時に外す。
// `<section aria-label="ブラシ">` は a11y のため残す。
//
// 以下、旧実装のコメントをそのまま残す (§4.1 の一般ルール等、経緯の記録として有効):
//
// ## 構造の作り直し (実機フィードバック「無駄な縦スペースが多い」、2026-08-17)
//
// 初版は `MODERN_BRUSH_SUBTOOLS` の各エントリ (ブラシ/サインペン/テクスチャ/エアブラシ)
// を「バケット見出し + その唯一の item」として並べていたため、**すべてのバケットの中身が
// 1個しかなく見出しが唯一の item と同じことを言う** (item ラベルはハードコード「既定」)
// 冗長な構造になっていた。以下の一般ルールで作り直す:
//
// 1. **見出しは「名前を持つグループ」にだけ出す** (`group.label !== null`)。単独ペン
//    (ブラシ/サインペン/エアブラシ) は名前を持たない 1 グループにまとめ、複数バリアント
//    を持つブラシ (テクスチャ) だけが名前付きグループになる。
// 2. **item ラベルは常にその item 自身の名前** (「既定」のようなプレースホルダは撤去)。
// 3. **テクスチャの4プリセット (紙/毛先/雲/カケアミ) を item として展開**する
//    (`TEXTURE_PRESET_OPTIONS` を再利用、`TexturePresetPicker` と同じ4種を重複定義しない)。
// 4. **表示切替はアイコン** (`≡`/`⊞`) にして横幅を空ける。
//
// **マイブラシ (§4 / §11)**: カスタムブラシ本体は別案件 (§3 着手時) だが、器だけ
// 常時表示する。スクロール領域の**外**に固定 (2026-08-19 実機フィードバック続報)。
//
// **メモ化**: item 数ぶん `renderBrushSample` を呼ぶため、太さ/不透明度/筆圧が変わる
// たびに再計算が走る。「計算 (renderBrushSample)」と「描画 (putImageData)」を分離し、
// 計算は `useMemo` で 1 箇所に集約する (= 本ファイルは全 item を常時表示する構成なので
// 「アクティブタブぶんだけ」のような絞り込みは対象外 — タブ版との差)。
//
// **a11y**: item 群は「複数の排他選択肢から 1 つを選ぶ」ボタン群。
// `role="radio"` (`<input type="radio">` を要求する biome `useSemanticElements` の制約) を
// 避けた経緯があるので、`<button aria-pressed>` 群の慣行を踏襲する。

import { useEffect, useMemo, useRef } from 'react';
import { TEXTURE_PRESET_OPTIONS } from '../shared/TexturePresetPicker.js';
import { BrushPanelItemButton } from './BrushPanelItemButton.js';
import {
  BRUSH_PANEL_ITEMS_MAX_HEIGHT,
  type BrushPanelItem,
  computeBrushPanelSamples,
  ITEMS_CONTAINER_CLASS,
  type ItemSample,
  type ModernBrushPanelProps,
} from './brushPanelShared.js';
import { MODERN_BRUSH_SUBTOOLS } from './modernTools.js';

export type { ModernBrushPanelProps as ModernBrushPanelFlatProps } from './brushPanelShared.js';

/**
 * 見出し (label) + item 群。**見出しは「名前を持つグループ」(`label !== null`) にだけ
 * 描画側で出す**。単独ペンをまとめた群は `label: null` (= 見出し無し)。
 */
interface BrushPanelGroup {
  readonly label: string | null;
  readonly items: ReadonlyArray<BrushPanelItem>;
}

/**
 * `MODERN_BRUSH_SUBTOOLS` から導出するグループ一覧。texture 以外 (brush/flat/airbrush)
 * は名前無し1グループにまとめる。texture だけ `TEXTURE_PRESET_OPTIONS` の 4件を item
 * として展開した名前付きグループにする。
 */
const BRUSH_PANEL_GROUPS: ReadonlyArray<BrushPanelGroup> = (() => {
  const unnamedItems: BrushPanelItem[] = [];
  let textureLabel: string | null = null;
  for (const sub of MODERN_BRUSH_SUBTOOLS) {
    if (sub.pen === 'texture') {
      textureLabel = sub.label;
      continue;
    }
    unnamedItems.push({ key: sub.pen, label: sub.label, pen: sub.pen });
  }
  const groups: BrushPanelGroup[] = [];
  if (unnamedItems.length > 0) {
    groups.push({ label: null, items: unnamedItems });
  }
  if (textureLabel !== null) {
    groups.push({
      label: textureLabel,
      items: TEXTURE_PRESET_OPTIONS.map((opt) => ({
        key: `texture:${opt.id}`,
        label: opt.label,
        pen: 'texture' as const,
        texturePreset: opt.id,
      })),
    });
  }
  return groups;
})();

/** 全 item を平坦化した一覧 (= サムネ計算・canvas ref 管理に使う)。 */
const BRUSH_PANEL_ITEMS: ReadonlyArray<BrushPanelItem> = BRUSH_PANEL_GROUPS.flatMap((g) => g.items);

/**
 * フォン版ブラシパネルの本体。グループ (ブラシ/サインペン/エアブラシ/テクスチャ) の
 * item 行をリスト表示 (固定) で出す。見出しは 2 item 以上のグループにだけ出す。末尾に
 * 「マイブラシ」(空、器だけ) を常時表示する。**タブは持たない**
 * (`ModernBrushPanel` = デスクトップ版との差、上部コメント参照)。
 */
export const ModernBrushPanelFlat = ({
  pen,
  onSelectPen,
  onSelectTexturePreset,
  width,
  alpha,
  pressure,
  texturePreset,
  airbrushGrain,
  airbrushDensity,
}: ModernBrushPanelProps): React.ReactNode => {
  const samples = useMemo<ReadonlyArray<ItemSample>>(
    () =>
      computeBrushPanelSamples(BRUSH_PANEL_ITEMS, {
        width,
        alpha,
        pressure: {
          widthEnabled: pressure.widthEnabled,
          widthMin: pressure.widthMin,
          alphaEnabled: pressure.alphaEnabled,
          alphaMin: pressure.alphaMin,
        },
        airbrushGrain,
        airbrushDensity,
      }),
    [
      width,
      alpha,
      pressure.widthEnabled,
      pressure.widthMin,
      pressure.alphaEnabled,
      pressure.alphaMin,
      airbrushGrain,
      airbrushDensity,
    ],
  );

  const canvasRefs = useRef<Map<string, HTMLCanvasElement>>(new Map());
  useEffect(() => {
    for (const s of samples) {
      const canvas = canvasRefs.current.get(s.key);
      if (!canvas) continue;
      const ctx = canvas.getContext('2d');
      if (!ctx) continue;
      ctx.putImageData(new ImageData(s.sample.rgba, s.sample.width, s.sample.height), 0, 0);
    }
  }, [samples]);

  const selectItem = (item: BrushPanelItem): void => {
    onSelectPen(item.pen);
    if (item.texturePreset !== undefined) onSelectTexturePreset(item.texturePreset);
  };

  return (
    <section
      className="flex flex-col gap-1.5 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-2"
      aria-label="ブラシ"
    >
      <div
        className="flex min-h-0 flex-col gap-1.5 overflow-y-auto overscroll-contain"
        style={{ maxHeight: BRUSH_PANEL_ITEMS_MAX_HEIGHT }}
      >
        <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0" aria-label="ブラシ選択">
          {BRUSH_PANEL_GROUPS.map((group, groupIndex) => (
            <div key={group.label ?? `unnamed-${groupIndex}`} className="flex flex-col gap-0.5">
              {group.label !== null && (
                <h3 className="select-none text-[10px] font-bold uppercase tracking-wide text-[var(--color-text-muted)]">
                  {group.label}
                </h3>
              )}
              <div className={ITEMS_CONTAINER_CLASS}>
                {group.items.map((item) => {
                  const active =
                    pen === item.pen &&
                    (item.texturePreset === undefined || item.texturePreset === texturePreset);
                  return (
                    <BrushPanelItemButton
                      key={item.key}
                      item={item}
                      active={active}
                      onSelect={selectItem}
                      canvasRef={(el) => {
                        if (el) canvasRefs.current.set(item.key, el);
                        else canvasRefs.current.delete(item.key);
                      }}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </fieldset>
      </div>

      <div className="border-t border-[var(--color-border)] pt-1.5" data-manual="draw.my-brush">
        <h3 className="select-none text-[10px] font-bold uppercase tracking-wide text-[var(--color-text-muted)]">
          マイブラシ (準備中)
        </h3>
      </div>
    </section>
  );
};
